const DISTANCEMATRIX_KEY = "pYZWpdCtsxwAqdk9qUkmRBRF4mSKEbFF5tN3Q8xHAtSl7IAeMd5Sptd4Lg0PjHRW";

/**
 * Get distance between two addresses
 * @param {string} start - Starting address
 * @param {string} end - Destination address
 * @returns {Object} - Distance in kilometers and formatted destination address
 */
export const getDistanceOfAddresses = async (start, end) => {
  try {
    if (!start || !end) {
      throw new Error("Both start and end addresses are required");
    }

    const url = `https://api.distancematrix.ai/maps/api/distancematrix/json?origins=${encodeURIComponent(
      start
    )}&destinations=${encodeURIComponent(
      end
    )}&language=en&traffic_model=optimistic&key=${DISTANCEMATRIX_KEY}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status !== "OK") {
      throw new Error(`Distance Matrix API error: ${data.status}`);
    }

    if (data.rows[0]?.elements[0]?.status !== "OK") {
      throw new Error(
        `Invalid address: ${data.rows[0]?.elements[0]?.status || "Unable to locate the given address"}`
      );
    }

    const element = data.rows[0].elements[0];
    
    return {
      distance: {
        value: element.distance.value / 1000, // Convert meters to kilometers
        text: element.distance.text,
      },
      duration: {
        value: element.duration.value, // Duration in seconds
        text: element.duration.text,
      },
      originAddress: data.origin_addresses[0],
      destinationAddress: data.destination_addresses[0],
    };
  } catch (err) {
    console.error("Error fetching distance:", err.message);
    throw err;
  }
};

/**
 * Get coordinates (latitude, longitude) of a given address
 * @param {string} address - Address to geocode
 * @returns {Object} - Coordinates and formatted address
 */
export const getCoordinateOfAddress = async (address) => {
  try {
    if (!address) {
      throw new Error("Address is required");
    }

    const url = `https://api.distancematrix.ai/maps/api/geocode/json?address=${encodeURIComponent(
      address
    )}&key=${DISTANCEMATRIX_KEY}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status !== "OK") {
      throw new Error(`Geocoding API error: ${data.status}`);
    }

    if (!data.result || data.result.length === 0) {
      throw new Error("No results found for the given address");
    }

    const result = data.result[0];
    
    return {
      coordinates: {
        latitude: result.geometry.location.lat,
        longitude: result.geometry.location.lng,
      },
      formattedAddress: result.formatted_address,
      addressComponents: result.address_components,
      placeId: result.place_id,
      types: result.types,
    };
  } catch (err) {
    console.error("Error fetching coordinates:", err.message);
    throw err;
  }
};

/**
 * Get coordinates for multiple addresses
 * @param {Array<string>} addresses - Array of addresses to geocode
 * @returns {Array<Object>} - Array of coordinates and formatted addresses
 */
export const getCoordinatesOfAddresses = async (addresses) => {
  try {
    if (!addresses || !Array.isArray(addresses) || addresses.length === 0) {
      throw new Error("Array of addresses is required");
    }

    const results = await Promise.allSettled(
      addresses.map(address => getCoordinateOfAddress(address))
    );

    return results.map((result, index) => {
      if (result.status === "fulfilled") {
        return {
          address: addresses[index],
          success: true,
          data: result.value,
        };
      } else {
        return {
          address: addresses[index],
          success: false,
          error: result.reason.message,
        };
      }
    });
  } catch (err) {
    console.error("Error fetching multiple coordinates:", err.message);
    throw err;
  }
};

/**
 * Reverse geocode: Get address from coordinates
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {Object} - Address information
 */
export const getAddressFromCoordinates = async (latitude, longitude) => {
  try {
    if (latitude === undefined || longitude === undefined) {
      throw new Error("Both latitude and longitude are required");
    }

    const url = `https://api.distancematrix.ai/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${DISTANCEMATRIX_KEY}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status !== "OK") {
      throw new Error(`Reverse Geocoding API error: ${data.status}`);
    }

    if (!data.result || data.result.length === 0) {
      throw new Error("No results found for the given coordinates");
    }

    const result = data.result[0];
    
    return {
      formattedAddress: result.formatted_address,
      addressComponents: result.address_components,
      placeId: result.place_id,
      types: result.types,
    };
  } catch (err) {
    console.error("Error fetching address from coordinates:", err.message);
    throw err;
  }
};

/**
 * Calculate distance between two coordinates
 * @param {Object} origin - Origin coordinates {latitude, longitude}
 * @param {Object} destination - Destination coordinates {latitude, longitude}
 * @returns {Object} - Distance and duration information
 */
export const getDistanceFromCoordinates = async (origin, destination) => {
  try {
    if (!origin?.latitude || !origin?.longitude || !destination?.latitude || !destination?.longitude) {
      throw new Error("Both origin and destination coordinates are required");
    }

    const originStr = `${origin.latitude},${origin.longitude}`;
    const destinationStr = `${destination.latitude},${destination.longitude}`;

    const url = `https://api.distancematrix.ai/maps/api/distancematrix/json?origins=${originStr}&destinations=${destinationStr}&language=en&traffic_model=optimistic&key=${DISTANCEMATRIX_KEY}`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status !== "OK") {
      throw new Error(`Distance Matrix API error: ${data.status}`);
    }

    if (data.rows[0]?.elements[0]?.status !== "OK") {
      throw new Error(
        `Invalid coordinates: ${data.rows[0]?.elements[0]?.status || "Unable to calculate distance"}`
      );
    }

    const element = data.rows[0].elements[0];
    
    return {
      distance: {
        value: element.distance.value / 1000, // Convert meters to kilometers
        text: element.distance.text,
      },
      duration: {
        value: element.duration.value, // Duration in seconds
        text: element.duration.text,
      },
    };
  } catch (err) {
    console.error("Error calculating distance from coordinates:", err.message);
    throw err;
  }
};